// Custom hook that owns all async track-level operations for the Studio page.
// Studio.jsx keeps recording/playback/UI concerns; this hook keeps the
// data-mutation and Web-Audio-baking logic in one place so Studio can stay lean.
//
// Contract:
//   const actions = useStudioActions({ project, setProject, projectId, tracks, setTracks });
//   → { drumifyingId, syncingId, busyId, quantizingAll, exporting,
//       deleteTrack, patchTrack, drumify, replaceWithInstrument, chordifyAndSave,
//       applyBarEditsAndSave, runSync, quantizeAll, exportFile,
//       nudgeOnsetAndSave, deleteOnsetAndSave, duplicateOnsetAndSave,
//       duplicateOnsetToBarsAndSave,
//       setBpm, toggleCountIn, toggleShare }
//
// Every "* AndSave" handler produces a NON-DESTRUCTIVE sibling track using the
// existing audio.js pipeline (offline render → WAV → /api/upload → POST track).

import { useState, useRef } from "react";
import { toast } from "sonner";
import JSZip from "jszip";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import {
  audioBufferToWav, detectOnsets, detectMelodyNotes, synthesizeMelody,
  detectChords, synthesizeChords, synthesizeDrumsFromOnsets, syncAudioToBpm,
  applyNoteRingLength, stretchBufferToTempo,
  applyBarEdits, getBarSeconds, getPeaks, nudgeOnset, deleteOnsetSegment,
  duplicateOnset, duplicateOnsetToTimes, snapAllOnsetsToGrid,
  deleteManyOnsetSegments, duplicateManyOnsets, nudgeManyOnsets, pasteOnsetsAtTimes,
  pitchShiftOnset, pitchShiftManyOnsets, autoTuneOnsets, previewNoteSegments, copyFirstHalfToSecondHalf,
  renderMixToWav, renderMixToMp3, renderStemToBlob,
  DRUM_KITS, INSTRUMENTS, CHORD_INSTRUMENTS,
} from "@/lib/audio";

// Fixed 4-bar loop window (shared with Studio.jsx for playback + auto-stop-record).
export const LOOP_BARS = 4;

// Extract cached onset times for a decoded buffer, skipping the FFT centroid pass.
export function onsetTimes(buffer) {
  if (!buffer) return [];
  try {
    return detectOnsets(buffer, { withCentroid: false }).map((o) => o.time);
  } catch (e) {
    logger.warn("onset detect failed:", e?.message);
    return [];
  }
}

async function uploadWav(buf, filename) {
  const wavBlob = audioBufferToWav(buf);
  const fd = new FormData();
  fd.append("file", wavBlob, filename);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

function buildTrackDoc(base, buf, up, extra = {}) {
  return {
    name: base.name,
    kind: base.kind,
    storage_path: up.path,
    duration: buf.duration,
    volume: 1, muted: false, solo: false,
    mime_type: "audio/wav",
    ...extra,
  };
}

export function useStudioActions({ project, setProject, projectId, tracks, setTracks }) {
  const [drumifyingId, setDrumifyingId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [quantizingAll, setQuantizingAll] = useState(false);
  const [snappingAll, setSnappingAll] = useState(false);
  const [exporting, setExporting] = useState(false);

  // ---------- Undo (multi-step) ----------
  // Ref (not state) so pushing a snapshot doesn't trigger a re-render, and so
  // mutating operations that happen inside async flows still see the latest
  // stack. Holds a discriminated union: {type,...} — see `undoLast` for the
  // dispatch table.
  // Capped at 10 steps: replaceBuffer entries retain full AudioBuffers, so an
  // unbounded stack would grow browser memory by megabytes per edit.
  const UNDO_MAX = 10;
  const undoStackRef = useRef([]);
  const undoBusyRef = useRef(false);
  const [undoAvailable, setUndoAvailable] = useState(false);
  function captureUndo(entry) {
    undoStackRef.current.push(entry);
    if (undoStackRef.current.length > UNDO_MAX) undoStackRef.current.shift();
    setUndoAvailable(true);
  }
  function clearUndo() {
    undoStackRef.current = [];
    setUndoAvailable(false);
  }

  // ---------- CRUD ----------
  async function deleteTrack(id) {
    try {
      await api.delete(`/tracks/${id}`);
      setTracks((p) => p.filter((t) => t.id !== id));
      toast.success("Track deleted");
    } catch (e) {
      logger.error("Delete failed:", e?.message || e);
      toast.error("Failed to delete");
    }
  }

  async function patchTrack(id, patch, opts = {}) {
    let prevSnapshot = null;
    setTracks((prev) => prev.map((t) => {
      if (t.id !== id) return t;
      prevSnapshot = Object.fromEntries(Object.keys(patch).map((k) => [k, t[k]]));
      return { ...t, ...patch };
    }));
    // Push an undo entry unless the caller asks us to skip (e.g. this is
    // itself being called from `undoLast` to reverse an earlier patch).
    if (prevSnapshot && !opts.skipUndo) {
      captureUndo({ type: "patchTrack", trackId: id, prevFields: prevSnapshot });
    }
    try {
      await api.patch(`/tracks/${id}`, patch);
    } catch (err) {
      logger.warn("Patch track failed, rolling back:", err?.message);
      if (prevSnapshot) {
        setTracks((prev) => prev.map((t) => (t.id === id ? { ...t, ...prevSnapshot } : t)));
      }
      toast.error("Update failed");
    }
  }

  // Shared post-render finaliser: upload + create track + push into local state.
  async function pushSibling(sourceTrack, buf, name, extra = {}) {
    const up = await uploadWav(buf, "edited.wav");
    const doc = buildTrackDoc({ name, kind: sourceTrack.kind }, buf, up, extra);
    const { data: created } = await api.post(`/projects/${projectId}/tracks`, doc);
    setTracks((prev) => [...prev, { ...created, buffer: buf, peaks: getPeaks(buf), onsets: onsetTimes(buf) }]);
    // Undo of a sibling add = delete the new track.
    captureUndo({ type: "addTrack", trackId: created.id });
  }

  // In-place buffer replacement: used by all note-level edits (nudge / delete /
  // duplicate / batch) so editing notes never spawns a duplicate row. The old
  // WAV in object storage is intentionally orphaned — cheap trade-off vs the UX
  // cost of having every edit fork a new track.
  async function replaceTrackBuffer(track, buf) {
    // Snapshot BEFORE mutating so undo can restore the exact prior buffer + fields.
    const prev = {
      trackId: track.id,
      storage_path: track.storage_path,
      duration: track.duration,
      mime_type: track.mime_type,
      buffer: track.buffer,
      peaks: track.peaks,
      onsets: track.onsets,
    };
    const up = await uploadWav(buf, "edited.wav");
    const patch = {
      storage_path: up.path,
      duration: buf.duration,
      mime_type: up.content_type || "audio/wav",
    };
    await patchTrack(track.id, patch, { skipUndo: true });
    setTracks((prevTs) => prevTs.map((t) => (
      t.id === track.id
        ? { ...t, ...patch, buffer: buf, peaks: getPeaks(buf), onsets: onsetTimes(buf) }
        : t
    )));
    captureUndo({ type: "replaceBuffer", ...prev });
  }

  // ---------- Drum / Instrument / Chord synthesis ----------
  async function drumify(track, kit = "808", opts = {}) {
    if (!track.buffer) return false;
    const { silent = false, stripCrash = false } = opts;
    // One-tap groove runs 3 layered passes — only the first keeps crash accents
    // so a tagged hit doesn't play 3 stacked crashes.
    let overrides = track.hit_overrides || null;
    if (stripCrash && overrides) {
      overrides = Object.fromEntries(Object.entries(overrides).filter(([, v]) => v !== "crash"));
      if (Object.keys(overrides).length === 0) overrides = null;
    }
    setDrumifyingId(track.id);
    try {
      const onsets = detectOnsets(track.buffer);
      if (onsets.length < 2) {
        if (!silent) toast.error("Couldn't find enough hits in this take");
        return false;
      }
      const drumBuf = await synthesizeDrumsFromOnsets(track.buffer, onsets, project?.bpm || 120, kit, overrides);
      const kitLabel = (DRUM_KITS.find((k) => k.v === kit) || DRUM_KITS[0]).l;
      await pushSibling({ kind: "drums" }, drumBuf, `${kitLabel} drums (${track.name})`);
      if (!silent) toast.success(`${kitLabel} drums · ${onsets.length} hits`);
      return true;
    } catch (e) {
      logger.error("Drumify failed:", e?.message || e);
      if (!silent) toast.error("Drumify failed");
      return false;
    } finally {
      setDrumifyingId(null);
    }
  }

  // Batch macro — runs three drumify passes (808 kick + acoustic Snare +
  // Hi-Hat top line) on a single rhythm source. Suppresses per-kit toasts and
  // reports one aggregate result at the end.
  async function oneTapGroove(track) {
    if (!track || !track.buffer) return;
    // Kit list is hoisted so a future refactor can move it near DRUM_KITS.
    const GROOVE_MACRO_KITS = ["808", "snare-only", "hat-only"];
    setDrumifyingId(track.id);
    let okCount = 0;
    const failed = [];
    try {
      for (const kit of GROOVE_MACRO_KITS) {
        const ok = await drumify(track, kit, { silent: true, stripCrash: kit !== "808" });
        if (ok) {
          okCount += 1;
        } else {
          const label = (DRUM_KITS.find((k) => k.v === kit) || { l: kit }).l;
          failed.push(label);
        }
      }
    } finally {
      setDrumifyingId(null);
    }
    if (okCount === 0) {
      toast.error("Couldn't build a groove — try recording more hits");
    } else if (okCount < GROOVE_MACRO_KITS.length) {
      // Partial success — surface which kit(s) were skipped so it's not a silent
      // degradation. Warning tone (yellow) instead of green ✓ to signal degraded outcome.
      toast.warning(`Groove built · ${okCount} of ${GROOVE_MACRO_KITS.length} · ${failed.join(" + ")} skipped`);
    } else {
      toast.success("Groove built · kick + snare + hi-hat");
    }
  }

  async function replaceWithInstrument(track, instrument) {
    if (!track.buffer) return;
    setBusyId(track.id);
    try {
      const onsets = detectOnsets(track.buffer);
      if (onsets.length < 1) { toast.error("Couldn't find any pitched notes"); return; }
      const notes = detectMelodyNotes(track.buffer, onsets, track.note_lengths);
      if (notes.length < 1) { toast.error("Couldn't detect a melody"); return; }
      const buf = await synthesizeMelody(track.buffer, notes, instrument);
      if (!buf) { toast.error("Synthesis failed"); return; }
      const instLabel = (INSTRUMENTS.find((i) => i.v === instrument) || INSTRUMENTS[0]).l;
      await pushSibling({ kind: "melody" }, buf, `${instLabel} (${track.name})`);
      toast.success(`${instLabel} · ${notes.length} notes`);
    } catch (e) {
      logger.error("Instrument synth failed:", e?.message || e);
      toast.error("Instrument synth failed");
    } finally {
      setBusyId(null);
    }
  }

  async function chordifyAndSave(track, instrument) {
    if (!track.buffer) return;
    setBusyId(track.id);
    try {
      const onsets = detectOnsets(track.buffer);
      if (onsets.length < 1) { toast.error("Couldn't find any pitched moments"); return; }
      const chords = detectChords(track.buffer, onsets, {
        keyRoot: project?.key_root,
        keyMode: project?.key_mode,
        noteLengths: track.note_lengths,
      });
      if (chords.length < 1) { toast.error("Couldn't detect any chords"); return; }
      const buf = await synthesizeChords(track.buffer, chords, instrument);
      if (!buf) { toast.error("Chord synthesis failed"); return; }
      const instLabel = (CHORD_INSTRUMENTS.find((i) => i.v === instrument) || INSTRUMENTS.find((i) => i.v === instrument) || INSTRUMENTS[0]).l;
      await pushSibling({ kind: "melody" }, buf, `${instLabel} chords (${track.name})`);
      const namesPreview = chords.slice(0, 4).map((c) => c.name).join(" · ");
      toast.success(`${instLabel} chords · ${chords.length} (${namesPreview}${chords.length > 4 ? "…" : ""})`);
    } catch (e) {
      logger.error("Chordify failed:", e?.message || e);
      toast.error("Chordify failed");
    } finally {
      setBusyId(null);
    }
  }

  // ---------- Bar-level edit ----------
  async function applyBarEditsAndSave(track, plan) {
    if (!track.buffer) return;
    setBusyId(track.id);
    try {
      const buf = await applyBarEdits(track.buffer, project?.bpm || 120, plan);
      if (!buf) { toast.error("Edit produced empty audio"); return; }
      await replaceTrackBuffer(track, buf);
      toast.success(`Edited · ${plan.length} bars`);
    } catch (e) {
      logger.error("Bar edit failed:", e?.message || e);
      toast.error("Bar edit failed");
    } finally {
      setBusyId(null);
    }
  }

  // ---------- Note-level edit ----------
  async function nudgeOnsetAndSave(track, onsetIndex, newTime) {
    if (!track.buffer) return;
    setBusyId(track.id);
    try {
      const buf = nudgeOnset(track.buffer, track.onsets || [], onsetIndex, newTime, track.note_lengths);
      if (!buf) { toast.error("Note didn't move far enough to matter"); return; }
      await replaceTrackBuffer(track, buf);
      const dtMs = Math.round((newTime - (track.onsets?.[onsetIndex] ?? 0)) * 1000);
      toast.success(`Note nudged · ${dtMs >= 0 ? "+" : ""}${dtMs}ms`);
    } catch (e) {
      logger.error("Nudge failed:", e?.message || e);
      toast.error("Nudge failed");
    } finally {
      setBusyId(null);
    }
  }

  async function deleteOnsetAndSave(track, onsetIndex) {
    if (!track.buffer) return;
    setBusyId(track.id);
    try {
      const buf = deleteOnsetSegment(track.buffer, track.onsets || [], onsetIndex, track.note_lengths);
      if (!buf) { toast.error("Note too short to delete"); return; }
      await replaceTrackBuffer(track, buf);
      toast.success("Note deleted");
    } catch (e) {
      logger.error("Delete note failed:", e?.message || e);
      toast.error("Delete failed");
    } finally {
      setBusyId(null);
    }
  }

  async function duplicateOnsetAndSave(track, onsetIndex) {
    if (!track.buffer) return;
    const orig = (track.onsets || [])[onsetIndex];
    if (typeof orig !== "number") return;
    // Offset by ONE grid step (matches the track's Snap setting) so the
    // duplicate lands visibly clear of the original — e.g. a beat away when
    // snap is 1/4, a sixteenth when snap is 1/16. Previously hardcoded to
    // 1/16 which looked like it stacked directly on top at coarser snaps and
    // on narrow (mobile) timelines.
    const bpm = project?.bpm || 120;
    const div = track.grid_division || 16;
    const step = getBarSeconds(bpm) / div;
    const newTime = Math.min((track.duration || 0) - 0.05, orig + step);
    setBusyId(track.id);
    try {
      const buf = duplicateOnset(track.buffer, track.onsets || [], onsetIndex, newTime, track.note_lengths);
      if (!buf) { toast.error("No room to duplicate this note"); return; }
      await replaceTrackBuffer(track, buf);
      toast.success(`Note duplicated · +${Math.round(step * 1000)}ms`);
    } catch (e) {
      logger.error("Duplicate note failed:", e?.message || e);
      toast.error("Duplicate failed");
    } finally {
      setBusyId(null);
    }
  }

  async function duplicateOnsetToBarsAndSave(track, onsetIndex, targetBars) {
    if (!track.buffer) return;
    const onsets = track.onsets || [];
    const orig = onsets[onsetIndex];
    if (typeof orig !== "number") return;
    const barSec = getBarSeconds(project?.bpm || 120);
    if (barSec <= 0) return;
    const srcBar = Math.floor(orig / barSec);
    const offsetInBar = orig - srcBar * barSec;
    const loopDur = (project?.loop_bars || LOOP_BARS) * barSec;
    const targets = [];
    for (const b of targetBars) {
      if (b === srcBar) continue;
      const t = b * barSec + offsetInBar;
      if (t < 0 || t >= (track.duration || 0) || t >= loopDur) continue;
      targets.push(t);
    }
    if (targets.length === 0) { toast.error("No valid target bars"); return; }
    setBusyId(track.id);
    try {
      const buf = duplicateOnsetToTimes(track.buffer, onsets, onsetIndex, targets, track.note_lengths);
      if (!buf) { toast.error("Couldn't duplicate this note"); return; }
      const label = targetBars.length === 1 ? `bar ${targetBars[0] + 1}` : `${targetBars.length} bars`;
      await replaceTrackBuffer(track, buf);
      toast.success(`Note copied to ${label}`);
    } catch (e) {
      logger.error("Multi-bar duplicate failed:", e?.message || e);
      toast.error("Duplicate failed");
    } finally {
      setBusyId(null);
    }
  }

  // ---------- Batch note-level operations (Shift-click multi-select) ----------
  async function deleteManyOnsetsAndSave(track, indices) {
    if (!track || !track.buffer || !indices || indices.length === 0) return;
    setBusyId(track.id);
    try {
      const buf = deleteManyOnsetSegments(track.buffer, track.onsets || [], indices, track.note_lengths);
      if (!buf) { toast.error("Nothing to delete"); return; }
      await replaceTrackBuffer(track, buf);
      toast.success(`Deleted ${indices.length} note${indices.length === 1 ? "" : "s"}`);
    } catch (e) {
      logger.error("Batch delete failed:", e?.message || e);
      toast.error("Batch delete failed");
    } finally {
      setBusyId(null);
    }
  }

  async function duplicateManyOnsetsAndSave(track, indices) {
    if (!track || !track.buffer || !indices || indices.length === 0) return;
    const bpm = project?.bpm || 120;
    const barSec = getBarSeconds(bpm);
    const gridDivision = track.grid_division || 16;
    const shiftStep = barSec / gridDivision;
    setBusyId(track.id);
    try {
      const buf = duplicateManyOnsets(track.buffer, track.onsets || [], indices, shiftStep, track.note_lengths);
      if (!buf) { toast.error("Nothing to duplicate"); return; }
      await replaceTrackBuffer(track, buf);
      toast.success(`Duplicated ${indices.length} note${indices.length === 1 ? "" : "s"}`);
    } catch (e) {
      logger.error("Batch duplicate failed:", e?.message || e);
      toast.error("Batch duplicate failed");
    } finally {
      setBusyId(null);
    }
  }

  // Paste a copied selection at an arbitrary anchor time (chosen by clicking a
  // spot on the timeline): the earliest copied note lands on the anchor and the
  // rest keep their relative spacing.
  async function pasteNotesAtTimeAndSave(track, indices, anchorTime) {
    if (!track || !track.buffer || !indices || indices.length === 0) return;
    const barSec = getBarSeconds(project?.bpm || 120);
    if (barSec <= 0) return;
    const onsets = track.onsets || [];
    const times = indices.map((i) => onsets[i]).filter((t) => typeof t === "number");
    if (times.length === 0) return;
    const shift = anchorTime - Math.min(...times);
    if (Math.abs(shift) < 0.005) { toast.error("Notes are already at that spot — pick another"); return; }
    const loopDur = (project?.loop_bars || LOOP_BARS) * barSec;
    const pairs = [];
    let maxEnd = 0;
    for (const i of indices) {
      const orig = onsets[i];
      if (typeof orig !== "number") continue;
      const t = orig + shift;
      if (t < 0 || t >= loopDur) continue;
      pairs.push({ index: i, time: t });
      maxEnd = Math.max(maxEnd, t + 1.5);
    }
    if (pairs.length === 0) { toast.error("That spot is outside the loop — pick another"); return; }
    // Grow the take with silence when pasting past its end, capped at the loop
    // boundary so we never create a phantom extra bar.
    const outDuration = Math.min(loopDur, Math.max(track.buffer.duration, maxEnd));
    setBusyId(track.id);
    try {
      const buf = pasteOnsetsAtTimes(track.buffer, onsets, pairs, track.note_lengths, outDuration);
      if (!buf) { toast.error("Paste failed"); return; }
      await replaceTrackBuffer(track, buf);
      toast.success(`Pasted ${pairs.length} note${pairs.length === 1 ? "" : "s"}`);
    } catch (e) {
      logger.error("Paste failed:", e?.message || e);
      toast.error("Paste failed");
    } finally {
      setBusyId(null);
    }
  }

  // ---------- Pitch shift (fix off-key notes in place) ----------
  // Duration-preserving via granular OLA (see `pitchShiftSegment` in audio.js).
  // Common corrections are ±1 or ±2 semitones; also supports ±12 for creative
  // octave stunts. Uses the shared in-place `replaceTrackBuffer` so no sibling
  // track is spawned.
  async function pitchShiftOnsetAndSave(track, onsetIndex, semitones) {
    if (!track || !track.buffer) return;
    if (Math.abs(semitones) < 0.001) return;
    setBusyId(track.id);
    try {
      const buf = await pitchShiftOnset(track.buffer, track.onsets || [], onsetIndex, semitones, track.note_lengths);
      if (!buf) { toast.error("Pitch shift failed"); return; }
      // Instant audition of the shifted note — don't wait for the upload.
      previewNoteSegments(buf, track.onsets || [], [onsetIndex], track.note_lengths, track.volume ?? 1);
      await replaceTrackBuffer(track, buf);
      const sign = semitones > 0 ? "+" : "";
      toast.success(`Pitch ${sign}${semitones} semi`);
    } catch (e) {
      logger.error("Pitch shift failed:", e?.message || e);
      toast.error("Pitch shift failed");
    } finally {
      setBusyId(null);
    }
  }

  async function pitchShiftManyOnsetsAndSave(track, indices, semitones) {
    if (!track || !track.buffer || !indices || indices.length === 0) return;
    if (Math.abs(semitones) < 0.001) return;
    setBusyId(track.id);
    try {
      const buf = await pitchShiftManyOnsets(track.buffer, track.onsets || [], indices, semitones, track.note_lengths);
      if (!buf) { toast.error("Nothing to pitch-shift"); return; }
      // Instant audition of the shifted selection — don't wait for the upload.
      previewNoteSegments(buf, track.onsets || [], indices, track.note_lengths, track.volume ?? 1);
      await replaceTrackBuffer(track, buf);
      const sign = semitones > 0 ? "+" : "";
      toast.success(`Pitched ${indices.length} note${indices.length === 1 ? "" : "s"} · ${sign}${semitones} semi`);
    } catch (e) {
      logger.error("Batch pitch shift failed:", e?.message || e);
      toast.error("Batch pitch shift failed");
    } finally {
      setBusyId(null);
    }
  }

  async function autoTuneOnsetsAndSave(track, indices, keyRoot, keyMode) {
    if (!track || !track.buffer || !indices || indices.length === 0) return;
    setBusyId(track.id);
    try {
      const buf = await autoTuneOnsets(track.buffer, track.onsets || [], indices, keyRoot, keyMode, track.note_lengths);
      if (!buf) { toast.error("Nothing to auto-tune (or already in key)"); return; }
      await replaceTrackBuffer(track, buf);
      const KEY_NAMES = ["C","C♯","D","D♯","E","F","F♯","G","G♯","A","A♯","B"];
      toast.success(`Auto-tuned ${indices.length} note${indices.length === 1 ? "" : "s"} → ${KEY_NAMES[keyRoot]} ${keyMode}`);
    } catch (e) {
      logger.error("Auto-tune failed:", e?.message || e);
      toast.error("Auto-tune failed");
    } finally {
      setBusyId(null);
    }
  }

  // ---------- Group-drag: move ALL selected notes by the same delta ----------
  // Fires when the user drags a note that's part of a 2+-note selection. Delta
  // is computed in seconds from the drag origin; every selected note is shifted
  // in-place through the shared `nudgeManyOnsets` audio helper. Skipped if the
  // shift is < 5 ms so a stray click doesn't burn a re-render.
  async function nudgeManyOnsetsAndSave(track, indices, deltaSec) {
    if (!track || !track.buffer || !indices || indices.length === 0) return;
    if (Math.abs(deltaSec) < 0.005) return;
    setBusyId(track.id);
    try {
      const buf = nudgeManyOnsets(track.buffer, track.onsets || [], indices, deltaSec, track.note_lengths);
      if (!buf) { toast.error("Nothing moved"); return; }
      await replaceTrackBuffer(track, buf);
      const dtMs = Math.round(deltaSec * 1000);
      toast.success(`Moved ${indices.length} note${indices.length === 1 ? "" : "s"} · ${dtMs >= 0 ? "+" : ""}${dtMs}ms`);
    } catch (e) {
      logger.error("Group nudge failed:", e?.message || e);
      toast.error("Group move failed");
    } finally {
      setBusyId(null);
    }
  }

  // ---------- Resize a single note-block (right-edge drag) ----------
  // Persists a per-note length (seconds) into the track's `note_lengths` map
  // AND makes the audio ring exactly that long: shortening fades the tail to
  // silence at the new length (undoable buffer replace); lengthening lets the
  // natural tail ring again on the next conversion. All subsequent edit ops
  // (nudge / delete / duplicate) read this map via `_segmentMeta` so the audio
  // slice matches the visible block length. `newLength = null` removes any
  // override, reverting to the default cap.
  async function resizeOnsetAndSave(track, onsetIndex, newLength) {
    if (!track) return;
    const key = String(onsetIndex);
    const current = { ...(track.note_lengths || {}) };
    if (newLength === null || newLength === undefined) {
      if (!(key in current)) return;
      delete current[key];
      await patchTrack(track.id, { note_lengths: current });
      return;
    }
    const clamped = Math.max(0.02, Math.min(4.0, newLength));
    // De-dupe: if length is already stored ±5 ms, skip the PATCH.
    if (typeof current[key] === "number" && Math.abs(current[key] - clamped) < 0.005) return;
    current[key] = clamped;
    // Truncate the ring so the sound matches the block (no-op when the note
    // already ends earlier or was lengthened).
    if (track.buffer) {
      try {
        const buf = applyNoteRingLength(track.buffer, track.onsets || [], onsetIndex, clamped);
        if (buf) {
          await replaceTrackBuffer(track, buf);
          toast.success(`Note rings ${clamped.toFixed(2)}s`);
        }
      } catch (e) {
        logger.error("Ring-length trim failed:", e?.message || e);
      }
    }
    await patchTrack(track.id, { note_lengths: current });
  }

  // ---------- Note-level Drumify classification override ----------
  // Non-destructive: only updates the track's `hit_overrides` map. Next Drumify
  // run consumes it (kits without a hard lock — Acoustic / Lo-fi). Pass
  // `kind = null` (or "auto") to remove an existing override at that index.
  async function classifyOnsetAndSave(track, onsetIndex, kind) {
    if (!track) return;
    const key = String(onsetIndex);
    const current = { ...(track.hit_overrides || {}) };
    const isAuto = kind === null || kind === "auto";
    if (isAuto) {
      if (!(key in current)) return; // nothing to clear
      delete current[key];
    } else {
      if (!["kick", "snare", "hat", "crash"].includes(kind)) return;
      if (current[key] === kind) return; // no-op
      current[key] = kind;
    }
    // Optimistic local patch + backend PATCH via existing helper.
    await patchTrack(track.id, { hit_overrides: current });
    const label = isAuto ? "auto" : kind;
    toast.success(`Hit ${onsetIndex + 1} · ${label}`);
  }

  // ---------- Sync / Quantize ----------
  async function runSync(track, gridDivision = 16, bpmOverride = null) {
    if (!track || !track.buffer) return;
    if (track.is_synced) { toast.error("Already a synced track"); return; }
    const bpm = bpmOverride || project?.bpm || 120;
    setSyncingId(track.id);
    try {
      const result = await syncAudioToBpm(track.buffer, bpm, gridDivision);
      if (!result || !result.buffer) { toast.error("Not enough hits to sync"); return; }
      await pushSibling(track, result.buffer, `${track.name} · 1/${gridDivision}`, {
        is_synced: true,
        synced_from: track.id,
        grid_division: gridDivision,
      });
      toast.success(`Synced to 1/${gridDivision} · ${result.onsetCount} hits`);
    } catch (e) {
      logger.error("Sync failed:", e?.message || e);
      toast.error("Sync failed");
    } finally {
      setSyncingId(null);
    }
  }

  async function quantizeAll() {
    const unsynced = tracks.filter((t) => t.buffer && !t.is_synced);
    if (unsynced.length === 0) { toast.error("Every track is already synced"); return; }
    setQuantizingAll(true);
    let okCount = 0;
    for (const t of unsynced) {
      try { await runSync(t, 16); okCount += 1; }
      catch (err) { logger.warn(`Quantize-all skipped track ${t.id}:`, err?.message || err); }
    }
    setQuantizingAll(false);
    if (okCount > 0) toast.success(`Quantized ${okCount} track${okCount === 1 ? "" : "s"}`);
  }

  // ---------- Snap-all-notes-to-grid (batches every onset into a single render) ----------
  // Unlike `runSync` which time-stretches the whole track to the BPM grid,
  // this shifts each individual detected note to the nearest grid line —
  // respecting the track's own `grid_division` setting (1/4 · 1/8 · 1/16 · 1/32).
  async function snapAllNotesToGridAndSave(track, opts = {}) {
    if (!track || !track.buffer) return false;
    const { silent = false } = opts;
    const bpm = project?.bpm || 120;
    const barSec = getBarSeconds(bpm);
    const loopDur = (project?.loop_bars || LOOP_BARS) * barSec;
    const division = track.grid_division || 16;
    const snapStep = barSec / division;
    setBusyId(track.id);
    try {
      const buf = snapAllOnsetsToGrid(track.buffer, track.onsets || [], snapStep, loopDur);
      if (!buf) {
        if (!silent) toast.error("Every note is already on the grid");
        return false;
      }
      await pushSibling(track, buf, `${track.name} · 1/${division} snapped`);
      if (!silent) toast.success(`${(track.onsets || []).length} notes → 1/${division}`);
      return true;
    } catch (e) {
      logger.error("Snap-all failed:", e?.message || e);
      if (!silent) toast.error("Snap-all failed");
      return false;
    } finally {
      setBusyId(null);
    }
  }

  async function snapAllNotesInProject() {
    // Skip sources that already have a matching '· 1/N snapped' sibling to avoid
    // accumulating duplicate rows on repeat clicks (surfaced by the QA agent).
    const existingSiblingNames = new Set(tracks.map((t) => t.name));
    const eligible = tracks.filter((t) => {
      if (!t.buffer || t.is_synced) return false;
      if (!(t.onsets || []).length) return false;
      const expected = `${t.name} · 1/${t.grid_division || 16} snapped`;
      return !existingSiblingNames.has(expected);
    });
    if (eligible.length === 0) {
      toast.error("No new tracks to snap");
      return;
    }
    setSnappingAll(true);
    let okCount = 0;
    for (const t of eligible) {
      try {
        const ok = await snapAllNotesToGridAndSave(t, { silent: true });
        if (ok) okCount += 1;
      } catch (err) {
        logger.warn(`Snap-all-project skipped track ${t.id}:`, err?.message || err);
      }
    }
    setSnappingAll(false);
    if (okCount > 0) {
      toast.success(`Snapped ${okCount} track${okCount === 1 ? "" : "s"} to grid`);
    }
  }

  // Section copy (8-bar projects): duplicate bars 1–4 onto bars 5–8 in one tap.
  async function copySectionAndSave(track) {
    if (!track?.buffer) return;
    const barSec = getBarSeconds(project?.bpm || 120);
    const halfDur = 4 * barSec;
    setBusyId(track.id);
    try {
      const buf = copyFirstHalfToSecondHalf(track.buffer, halfDur);
      if (!buf) { toast.error("Section copy failed"); return; }
      await replaceTrackBuffer(track, buf);
      toast.success("Bars 1–4 copied onto 5–8");
    } catch (e) {
      logger.error("Section copy failed:", e?.message || e);
      toast.error("Section copy failed");
    } finally {
      setBusyId(null);
    }
  }

  // ---------- Export ----------
  async function exportFile(projectName, format) {
    if (tracks.length === 0) return;
    setExporting(true);
    try {
      const blob = format === "mp3" ? await renderMixToMp3(tracks) : await renderMixToWav(tracks);
      if (!blob) { toast.error("Nothing audible to export"); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(projectName || "beatbox").replace(/[^a-z0-9]+/gi, "_")}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${format.toUpperCase()}`);
    } catch (e) {
      logger.error("Export failed:", e?.message || e);
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  }

  // Stems export: render up to 3 selected tracks as individual files (shared
  // duration so they align in a DAW) and bundle them into one zip download.
  async function exportStems(projectName, trackIds, format = "wav") {
    const sel = tracks.filter((t) => trackIds.includes(t.id) && t.buffer).slice(0, 3);
    if (sel.length === 0) { toast.error("Pick at least one track"); return false; }
    setExporting(true);
    try {
      const nonSyncedDur = Math.max(0, ...sel.filter((t) => !t.is_synced).map((t) => t.buffer.duration));
      const syncedDur = Math.max(0, ...sel.filter((t) => t.is_synced).map((t) => t.buffer.duration));
      const duration = nonSyncedDur > 0 ? nonSyncedDur : syncedDur * 4;
      if (duration <= 0) { toast.error("Nothing audible to export"); return false; }
      const zip = new JSZip();
      for (let i = 0; i < sel.length; i++) {
        const blob = await renderStemToBlob(sel[i], duration, format);
        if (!blob) continue;
        const safe = (sel[i].name || `track_${i + 1}`).replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "") || `track_${i + 1}`;
        zip.file(`${String(i + 1).padStart(2, "0")}_${safe}.${format}`, blob);
      }
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      const proj = (projectName || "beatbox").replace(/[^a-z0-9]+/gi, "_");
      a.download = `${proj}_stems_${project?.bpm || 120}bpm.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${sel.length} stem${sel.length === 1 ? "" : "s"} · ${format.toUpperCase()} · ZIP`);
      return true;
    } catch (e) {
      logger.error("Stems export failed:", e?.message || e);
      toast.error("Stems export failed");
      return false;
    } finally {
      setExporting(false);
    }
  }

  // ---------- Project settings ----------
  async function patchProjectSettings(patch, successMsg) {
    // Snapshot prior values so undo can restore them.
    const prevFields = project
      ? Object.fromEntries(Object.keys(patch).map((k) => [k, project[k]]))
      : null;
    try {
      const { data: updated } = await api.patch(`/projects/${projectId}`, {
        name: project.name,
        bpm: project.bpm,
        description: project.description || "",
        auto_sync: project.auto_sync,
        count_in: project.count_in,
        key_root: project.key_root ?? 0,
        key_mode: project.key_mode ?? "major",
        loop_bars: project.loop_bars ?? 4,
        ...patch,
      });
      setProject(updated);
      if (prevFields) captureUndo({ type: "patchProject", prevFields });
      if (successMsg) toast.success(successMsg);
      return updated;
    } catch (e) {
      logger.error("patchProjectSettings failed:", e?.message || e);
      toast.error("Failed to update");
      return null;
    }
  }

  // ---------- Undo (dispatch) ----------
  async function undoLast() {
    // Serialize rapid Ctrl+Z presses — each undo awaits a server patch, and
    // overlapping reverts would race each other.
    if (undoBusyRef.current) return;
    const entry = undoStackRef.current.pop();
    setUndoAvailable(undoStackRef.current.length > 0);
    if (!entry) { toast("Nothing to undo"); return; }
    const left = undoStackRef.current.length;
    const more = left > 0 ? ` · ${left} more` : "";
    undoBusyRef.current = true;
    try {
      if (entry.type === "patchTrack") {
        await patchTrack(entry.trackId, entry.prevFields, { skipUndo: true });
        toast.success(`Undo · field restored${more}`);
      } else if (entry.type === "replaceBuffer") {
        const revert = {
          storage_path: entry.storage_path,
          duration: entry.duration,
          mime_type: entry.mime_type,
        };
        await patchTrack(entry.trackId, revert, { skipUndo: true });
        setTracks((prev) => prev.map((t) => (
          t.id === entry.trackId
            ? { ...t, ...revert, buffer: entry.buffer, peaks: entry.peaks, onsets: entry.onsets }
            : t
        )));
        toast.success(`Undo · edit reverted${more}`);
      } else if (entry.type === "addTrack") {
        await api.delete(`/tracks/${entry.trackId}`);
        setTracks((prev) => prev.filter((t) => t.id !== entry.trackId));
        toast.success(`Undo · new track removed${more}`);
      } else if (entry.type === "patchProject") {
        // Send the reverse patch to the server WITHOUT recording it in the undo
        // stack (skipUndo=true style). Cheapest way: re-use patchProjectSettings
        // then discard the entry it just pushed.
        await patchProjectSettings(entry.prevFields, null);
        undoStackRef.current.pop();
        setUndoAvailable(undoStackRef.current.length > 0);
        toast.success(`Undo · project restored${more}`);
      }
    } catch (e) {
      logger.error("Undo failed:", e?.message || e);
      toast.error("Undo failed");
    } finally {
      undoBusyRef.current = false;
    }
  }

  const setBpm = (newBpm) => {
    if (!project || newBpm === project.bpm) return Promise.resolve(null);
    return patchProjectSettings({ bpm: newBpm }, `BPM set to ${newBpm}`);
  };

  // User-initiated BPM change: re-time EVERY recorded track to the new tempo
  // (pitch-preserving WSOLA stretch) so playback actually slows down / speeds
  // up — not just the grid visuals. The auto-BPM estimate after a first
  // recording still uses plain `setBpm` (that take was already AT that tempo).
  const changeBpm = async (newBpm) => {
    if (!project || newBpm === project.bpm) return null;
    const oldBpm = project.bpm || 120;
    const audioTracks = tracks.filter((t) => t.buffer);
    if (audioTracks.length === 0) return setBpm(newBpm);
    const tempo = newBpm / oldBpm;
    const toastId = toast.loading(`Re-timing ${audioTracks.length} track${audioTracks.length === 1 ? "" : "s"} to ${newBpm} BPM…`);
    try {
      for (const t of audioTracks) {
        const stretched = await stretchBufferToTempo(t.buffer, tempo);
        await replaceTrackBuffer(t, stretched);
        if (t.note_lengths && Object.keys(t.note_lengths).length > 0) {
          const scaled = Object.fromEntries(
            Object.entries(t.note_lengths).map(([k, v]) => [k, v / tempo])
          );
          await patchTrack(t.id, { note_lengths: scaled }, { skipUndo: true });
        }
      }
      await patchProjectSettings({ bpm: newBpm }, null);
      toast.success(`BPM ${oldBpm} → ${newBpm} · whole composition re-timed`, { id: toastId });
    } catch (e) {
      logger.error("BPM re-time failed:", e?.message || e);
      toast.error("BPM change failed — tracks may be partially re-timed. Press Ctrl+Z to undo.", { id: toastId });
    }
    return null;
  };
  const toggleCountIn = (value) =>
    patchProjectSettings({ count_in: value }, value ? "Count-in on" : "Count-in off");
  const setLoopBars = (value) => {
    const bars = value === 8 ? 8 : 4;
    return patchProjectSettings({ loop_bars: bars }, `Loop length · ${bars} bars`);
  };
  // Update the project's musical key. Accepts a partial payload —
  // { key_root } or { key_mode } or both — so the toolbar's two dropdowns
  // can save independently.
  const setKey = (partial) => {
    if (!project || !partial) return Promise.resolve(null);
    const clean = {};
    if (typeof partial.key_root === "number") clean.key_root = partial.key_root;
    if (typeof partial.key_mode === "string") clean.key_mode = partial.key_mode;
    if (Object.keys(clean).length === 0) return Promise.resolve(null);
    const KEY_NAMES = ["C","C♯","D","D♯","E","F","F♯","G","G♯","A","A♯","B"];
    const nextRoot = clean.key_root ?? project.key_root ?? 0;
    const nextMode = clean.key_mode ?? project.key_mode ?? "major";
    return patchProjectSettings(clean, `Key · ${KEY_NAMES[nextRoot]} ${nextMode}`);
  };

  async function toggleShare(value) {
    try {
      const { data } = await api.post(`/projects/${projectId}/share`, { is_public: value });
      setProject(data);
      toast.success(value ? "Project is now shareable" : "Sharing turned off");
    } catch (e) {
      logger.error("Share toggle failed:", e?.message || e);
      toast.error("Failed");
    }
  }

  return {
    // exposed state
    drumifyingId, syncingId, busyId, quantizingAll, snappingAll, exporting,
    // CRUD
    deleteTrack, patchTrack,
    // Synthesis
    drumify, oneTapGroove, replaceWithInstrument, chordifyAndSave,
    // Bar edit
    applyBarEditsAndSave,
    // Note edit
    nudgeOnsetAndSave, deleteOnsetAndSave, duplicateOnsetAndSave, duplicateOnsetToBarsAndSave,
    deleteManyOnsetsAndSave, duplicateManyOnsetsAndSave, classifyOnsetAndSave,
    nudgeManyOnsetsAndSave, resizeOnsetAndSave, pasteNotesAtTimeAndSave,
    pitchShiftOnsetAndSave, pitchShiftManyOnsetsAndSave, autoTuneOnsetsAndSave,
    // Sync / Quantize / Snap
    runSync, quantizeAll, snapAllNotesToGridAndSave, snapAllNotesInProject,
    // Export
    exportFile, exportStems, copySectionAndSave,
    // Project settings
    setBpm, changeBpm, toggleCountIn, toggleShare, setKey, setLoopBars,
    // Undo
    undoLast, undoAvailable,
  };
}
